import type { Dimension } from '@pubstack/common/src/analytics/dimension'
import { MappedName, ServerSideMetricsByDim } from '@pubstack/common/src/analytics/query'
import type { CurrencySymbol } from '@pubstack/common/src/currency'
import { type FunctionComponent, useMemo } from 'react'
import { createSearchParams, useNavigate } from 'react-router-dom'
import { useAnalyticsQueryTanstack } from '~/api/analytics/analytics-query.api'
import { MultipleDetailedDataTable } from '~/components/DataTable/DataTable'
import { DataTableDetailedPropertyProvider } from '~/components/DataTable/DataTableDetailedProperties'
import { Widget, type WidgetProps } from '~/components/Widget'
import { type AnalyticsDataTableConfigs, useAnalyticsDataTable } from '~/modules/analytics/analyticsDataTable.hooks'
import {
  adUnitCount,
  bidBidderCount,
  serverSideAuctionCount,
  serverSideBidCpm,
  serverSideBidDensity,
  serverSideBidRate,
  serverSideBidRequests,
  serverSideBidTimeoutRate,
  serverSideECPM,
  serverSideImpressionCount,
  serverSideImpressionRevenue,
  tagIdCount,
} from '~/modules/analytics/common/formulas'
import type { Context } from '~/state'
import type { WithClassName } from '~/types/utils'
import { ANALYTICS_TOOLTIPS } from '~/utils/constants'

const MAPPING_DIMENSION_LABEL: Record<string, string> = {
  tagId: 'sites',
  bidder: 'bidders',
  adUnit: 'ad units',
}

type ServerSideBreakdownProps = WithClassName &
  Omit<WidgetProps, 'title' | 'icon' | 'info' | 'refreshTrackingLabel'> & {
    context: Context
    currencySymbol: CurrencySymbol
    dimension: Extract<Dimension, 'tagId' | 'adUnit' | 'bidder'>
    dimensions: Dimension[]
    rawData?: ServerSideMetricsByDim
  }

const getDataConfigForDimension = (dimension: Extract<Dimension, 'tagId' | 'adUnit' | 'bidder'>): AnalyticsDataTableConfigs<string> => {
  const baseConfig = [
    {
      displayedAs: 'detail',
      getDetails: ({ data }: { data: any }) =>
        dimension === 'adUnit'
          ? [DataTableDetailedPropertyProvider.bidBidderCount(data.bidBidderCount.value), DataTableDetailedPropertyProvider.tagIdCount(data.tagIdCount.value)]
          : [DataTableDetailedPropertyProvider.tagIdCount(data.tagIdCount.value), DataTableDetailedPropertyProvider.adUnitCount(data.adUnitCount.value)],
      status: {
        alert: 30,
        warning: 60,
        defaultValue: 'success',
      },
      sortLabel: 'Name',
    },
    {
      formula: serverSideImpressionRevenue,
      propertyName: 'impressionRevenue',
      tooltip: ANALYTICS_TOOLTIPS.SERVER_SIDE_IMPRESSION_REVENUE,
    },
    {
      formula: serverSideImpressionCount,
      propertyName: 'impressionCount',
      tooltip: ANALYTICS_TOOLTIPS.SERVER_SIDE_IMPRESSIONS,
    },
    {
      formula: serverSideAuctionCount,
      propertyName: 'auctionCount',
      tooltip: ANALYTICS_TOOLTIPS.SERVER_SIDE_AUCTION_COUNT,
    },
    {
      formula: serverSideBidRequests,
      propertyName: 'bidRequestCount',
      tooltip: ANALYTICS_TOOLTIPS.SERVER_SIDE_AUCTION_COUNT,
    },
    {
      formula: serverSideECPM,
      propertyName: 'ecpm',
      tooltip: ANALYTICS_TOOLTIPS.PREBID_ECPM,
    },
    {
      formula: tagIdCount,
      propertyName: 'tagIdCount',
    },
    {
      formula: adUnitCount,
      propertyName: 'adUnitCount',
    },
    {
      formula: bidBidderCount,
      propertyName: 'bidBidderCount',
    },
  ]

  const dimensionConfigs: Record<Extract<Dimension, 'tagId' | 'adUnit' | 'bidder'>, any> = {
    bidder: [
      {
        formula: serverSideBidRate,
        propertyName: 'bidRate',
        tooltip: ANALYTICS_TOOLTIPS.SERVER_SIDE_BID_RATE,
      },
      {
        formula: serverSideBidCpm,
        propertyName: 'bidCpm',
        tooltip: ANALYTICS_TOOLTIPS.SERVER_SIDE_BIDDER_DETAILS_BID_CPM,
      },
      {
        formula: serverSideBidTimeoutRate,
        propertyName: 'bidTimeoutRate',
        tooltip: ANALYTICS_TOOLTIPS.SERVER_SIDE_BIDDER_DETAILS_TIMEOUT_RATE,
      },
    ],

    tagId: [
      {
        formula: serverSideBidDensity,
        propertyName: 'bidServerDensity',
        tooltip: ANALYTICS_TOOLTIPS.SERVER_SIDE_BID_DENSITY,
      },
    ],

    adUnit: [
      {
        formula: serverSideBidDensity,
        propertyName: 'bidServerDensity',
        tooltip: ANALYTICS_TOOLTIPS.SERVER_SIDE_BID_DENSITY,
      },
    ],
  }

  return [[...baseConfig, ...dimensionConfigs[dimension]]] as AnalyticsDataTableConfigs<string>
}

const getColumnsConfigForDimension = (dimension: Extract<Dimension, 'tagId' | 'adUnit' | 'bidder'>) => {
  const columnConfigs: Record<Extract<Dimension, 'tagId' | 'adUnit' | 'bidder'>, string[]> = {
    bidder: ['impressionRevenue', 'bidRequestCount', 'impressionCount', 'ecpm', 'bidRate', 'bidCpm', 'bidTimeoutRate'],
    tagId: ['impressionRevenue', 'auctionCount', 'impressionCount', 'ecpm', 'bidServerDensity'],
    adUnit: ['impressionRevenue', 'auctionCount', 'impressionCount', 'ecpm', 'bidServerDensity'],
  }

  return columnConfigs[dimension]
}

export const ServerSideBreakdown: FunctionComponent<ServerSideBreakdownProps> = ({ context, dimensions, currencySymbol, dimension, ...props }) => {
  const analyticsQueryByDim = useAnalyticsQueryTanstack<ServerSideMetricsByDim>('server-side.metrics.by.dim', dimensions, context, {
    dimension,
  })
  const navigate = useNavigate()

  const dataConfig = useMemo(() => getDataConfigForDimension(dimension), [dimension])
  const columnsConfig = useMemo(() => getColumnsConfigForDimension(dimension), [dimension])

  const { data, columns, sorting } = useAnalyticsDataTable({
    rawData: analyticsQueryByDim.data,
    currencySymbol,
    dataConfigs: dataConfig,
    columnsConfigs: columnsConfig,
  })

  const onRowClick = (bidderName: MappedName) => {
    if (dimension !== 'bidder') return
    navigate({
      pathname: `/analytics/server-side/${bidderName.label}`,
      search: `?${createSearchParams({
        from: context.timeRange.from.toFormat('yyyy-MM-dd'),
        to: context.timeRange.to.toFormat('yyyy-MM-dd'),
        mapping: btoa(
          JSON.stringify({
            bidder: bidderName.value,
            bidderName: bidderName.label,
          })
        ),
        dimension: 'bidder',
      })}`,
    })
  }

  return (
    <Widget
      {...props}
      isLoading={analyticsQueryByDim.isFetching}
      error={analyticsQueryByDim.isError}
      empty={!analyticsQueryByDim.isFetching && !analyticsQueryByDim?.data?.name.length}
      icon={'bidder'}
      title={`Overviews per ${MAPPING_DIMENSION_LABEL[dimension]}`}
      refreshTrackingLabel={'ana-server-side-refresh'}
    >
      <MultipleDetailedDataTable columns={columns} rows={data} sorting={sorting} onRowClick={dimension === 'bidder' ? ({ index }) => onRowClick(data[index][0].name) : undefined} />
    </Widget>
  )
}
